import { SkyBadge } from "@skypointcloud/skypoint-ui";
import type { ProgressTransport } from "@/hooks/use-run-progress";

/**
 * Where the live feed is, said out loud (FR-55).
 *
 * This badge exists because the alternative is a page that looks live and is
 * not. When the socket is down the badge says "Reconnecting" AND names the
 * fallback, so a user watching a long import knows the numbers are still
 * moving, just more slowly. It is a status region, so a screen reader hears the
 * change without the focus moving.
 */

export interface LiveIndicatorProps {
  transport: ProgressTransport;
  pollSeconds: number;
}

/** The badge for a transport: live, connecting, or polling while it reconnects. */
function indicatorOf(
  transport: Exclude<ProgressTransport, "disabled">,
  pollSeconds: number,
): { color: "success" | "informative" | "warning"; text: string } {
  if (transport === "live") return { color: "success", text: "Live" };
  if (transport === "connecting") return { color: "informative", text: "Connecting" };
  return {
    color: "warning",
    text: `Reconnecting, refreshing every ${pollSeconds} seconds`,
  };
}

export function LiveIndicator({ transport, pollSeconds }: LiveIndicatorProps) {
  if (transport === "disabled") return null;

  const { color, text } = indicatorOf(transport, pollSeconds);

  return (
    <span role="status" data-testid="run-live-indicator">
      <SkyBadge appearance="tint" color={color}>
        {text}
      </SkyBadge>
    </span>
  );
}
